import { useState, useRef, useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Badge } from './ui/Badge';
import { useAuth } from '@/lib/auth';
import {
  useUnreadAlertCount,
  useAlertHistory,
  useMarkAlertRead,
  useMarkAllAlertsRead,
} from '@/hooks/queries/useAlerts';
import { formatRelativeTime, formatAlertType } from '@/lib/formatters';
import { isPushSupported, isPushEnabled, registerPushSubscription } from '@/lib/push';
import { cn } from '@/lib/utils';
import { MarketStatusIndicator } from './ui/MarketStatusIndicator';
import { SymbolSearch } from './ui/SymbolSearch';
import { TenantBadge } from './TenantBadge';
import type { AlertSeverity } from '@steward/shared';

export function Layout() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <div className="lg:pl-64">
        {/* z-30: above every page layer. Page content uses z-10 (the chain page's current-price
            marker, ChainFlow, ChainJourney, the spinner overlay) and the chain page's sticky
            sub-header uses z-20. At an equal z-10 the later element in the DOM paints on top, so a
            price marker scrolling up used to slide behind the chain sub-header and then over this
            one. */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 lg:hidden">Steward</h1>
              <TenantBadge />
            </div>
            {/* gap-2 on a phone pays for the search box's extra width. */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto min-w-0">
              <SymbolSearch />
              <MarketStatusIndicator />
              <NotificationBell />
              <UserMenu user={user} />
            </div>
          </div>
        </header>
        <main className="p-4 pb-20 sm:p-6 lg:p-8 lg:pb-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { data: unreadCount = 0 } = useUnreadAlertCount();
  const { data: recentAlerts } = useAlertHistory({ limit: 10 });
  const markRead = useMarkAlertRead();
  const markAllRead = useMarkAllAlertsRead();
  const [pushState, setPushState] = useState<'unknown' | 'enabled' | 'disabled' | 'unsupported'>('unknown');

  // Check push state on mount
  useEffect(() => {
    if (!isPushSupported()) {
      setPushState('unsupported');
      return;
    }
    isPushEnabled()
      .then((enabled) => setPushState(enabled ? 'enabled' : 'disabled'))
      .catch(() => setPushState('disabled'));
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const severityVariants: Record<AlertSeverity, 'info' | 'warning' | 'error'> = {
    info: 'info',
    warning: 'warning',
    critical: 'error',
  };

  async function handleEnablePush() {
    const ok = await registerPushSubscription();
    setPushState(ok ? 'enabled' : 'disabled');
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded-lg"
        aria-label="View notifications"
        onClick={() => setOpen(!open)}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] max-h-[28rem] overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 z-50">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  className="text-xs text-brand-600 hover:text-brand-700"
                  onClick={() => markAllRead.mutate()}
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Push notification banner */}
          {pushState === 'disabled' && (
            <button
              onClick={handleEnablePush}
              className="flex w-full items-center gap-2 border-b border-gray-100 bg-blue-50 px-4 py-2.5 text-left text-xs text-blue-700 hover:bg-blue-100 transition-colors"
            >
              <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span>Enable push notifications to get alerts on your device</span>
            </button>
          )}

          {/* Alert list */}
          <div className="max-h-80 overflow-y-auto">
            {!recentAlerts || recentAlerts.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No notifications yet
              </div>
            ) : (
              recentAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={cn(
                    'block px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer',
                    !alert.isRead && 'bg-brand-50/50'
                  )}
                  onClick={() => {
                    if (!alert.isRead) markRead.mutate(alert.id);
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Badge variant={severityVariants[alert.severity]} size="sm">
                          {alert.severity}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {formatAlertType(alert.alertType)}
                        </span>
                        {!alert.isRead && (
                          <span className="h-1.5 w-1.5 rounded-full bg-brand-600 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-900 truncate">{alert.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{alert.message}</p>
                    </div>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0 mt-1">
                      {formatRelativeTime(alert.createdAt)}
                    </span>
                  </div>
                  {alert.tradeChainId && (
                    <Link
                      to={`/chains/${alert.tradeChainId}`}
                      className="text-xs text-brand-600 hover:text-brand-700 mt-1 inline-block"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen(false);
                      }}
                    >
                      View Chain
                    </Link>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 px-4 py-2.5">
            <Link
              to="/alerts/history"
              className="text-xs text-brand-600 hover:text-brand-700 font-medium"
              onClick={() => setOpen(false)}
            >
              View all alerts
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function UserMenu({ user }: { user: { email: string; name: string | null } | null }) {
  if (!user) return null;

  const initials = user.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user.email[0].toUpperCase();

  return (
    <div className="flex items-center gap-3">
      <div className="hidden sm:block text-right">
        <p className="text-sm font-medium text-gray-700">{user.name || user.email}</p>
        {user.name && <p className="text-xs text-gray-500">{user.email}</p>}
      </div>
      <div className="h-8 w-8 rounded-full bg-brand-600 flex items-center justify-center">
        <span className="text-sm font-medium text-white">{initials}</span>
      </div>
    </div>
  );
}
